import { type FC } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import IconWrapper from '@segment/matcha/components/IconWrapper'

import { CheckIcon, NamingConventionsIcon, ToolIcon } from '@segment/matcha/components/Icons/Brandbook'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Learn more about increasing customer lifetime value with Segment`,
}

const getData = ({ pageConfig }: { pageConfig: PageConfig }) => {
  return [
    {
      icon: CheckIcon,
      heading: `Drive repeat purchasers with a personalized re-engagement Journey`,
      description: `How to design a multi-step re-engagement campaign with Journeys, a feature within Twilio Engage.`,
      button: {
        text: `Read the recipe`,
        href: `${getAbsoluteUrl(`/recipes/journeys-reengagement-campaign/`, pageConfig)}`,
      },
    },
    {
      icon: NamingConventionsIcon,
      heading: `How to maximize ROI with AI-driven personalization`,
      description: `Watch how Twilio Segment’s customer data platform empowers businesses to deliver personalized omnichannel engagement at scale with AI.`,
      button: {
        text: `Watch the webinar`,
        href: `https://webinars.segment.com/segment/how-to-maximize-roi-with-ai-driven-personalization`,
      },
    },
    {
      icon: ToolIcon,
      heading: `The ultimate guide to customer retention for 2024`,
      description: `Learn how zeroing in on customer retention and doubling down on existing software, like a CDP, enables companies to achieve business objectives with fewer resources and less ad spend.`,
      button: {
        text: `Get the guide`,
        href: `${getAbsoluteUrl(`/learn/download/the-ultimate-guide-to-customer-retention-for-2024/`, pageConfig)}`,
      },
    },
  ]
}

const LearnMore: FC = () => {
  const pageConfig = usePageConfig()
  const data = getData({ pageConfig })

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
        </Section.Copy>

        <Section.Contents>
          <ColumnTrack columns={1} overrides={{ md: 3 }}>
            {data.map(({ icon: Icon, heading, description, button }) => (
              <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
                <FeatureBox.Header>
                  <StyledIconWrapper variant="circle" elevation={1}>
                    <Icon size="large" />
                  </StyledIconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{heading}</FeatureBox.Title>
                  <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                </FeatureBox.Body>
                <ButtonText href={button.href} iconEnd={ChevronRightIcon}>
                  {button.text}
                </ButtonText>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const StyledIconWrapper = styled(IconWrapper)`
  width: 64px;
  height: 64px;
  padding: 6px;
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.mint[10]};
`

const StyledSection = styled(SectionBase)`
  max-width: 1280px;
`

export default LearnMore
